/**
 * ROROS Meetings — React Query Hooks (M-Rhythm-2 + M-Rhythm-3)
 *
 * M-Rhythm-2: Read-only hooks (calendar, today).
 * M-Rhythm-3: Mutation hooks (create ad-hoc, update, skip, add/remove participant, set agenda).
 *
 * All mutations use apiFetch (no raw fetch).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import type { CalendarMeetingDTO, CalendarResponse, RorosMeetingDTO } from '@/types/roros/meetings';

/** F2.2: typed API error shape — avoids `as any` casts on error responses. */
interface ApiErrorResponse { error?: { message?: string; code?: string } }

const STALE_TIME = 2 * 60_000; // 2 minutes
const GC_TIME = 5 * 60_000;

// ─── Query Keys ──────────────────────────────────────────────────────────────

export const meetingKeys = {
  all: ['roros', 'meetings'] as const,
  list: (params?: Record<string, string>) => ['roros', 'meetings', 'list', params] as const,
  calendar: (from: string, to: string) => ['roros', 'meetings', 'calendar', from, to] as const,
  today: () => ['roros', 'meetings', 'today'] as const,
  detail: (id: string) => ['roros', 'meetings', 'detail', id] as const,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function fetchCalendar(from: string, to: string): Promise<CalendarMeetingDTO[]> {
  const res = await apiFetch(`/api/v1/meetings/calendar?from=${from}&to=${to}`);
  if (!res.ok) throw new Error(`Calendar fetch failed: ${res.status}`);
  const json = await res.json() as { data: CalendarResponse };
  return json.data.meetings;
}

async function fetchToday(): Promise<CalendarMeetingDTO[]> {
  const res = await apiFetch('/api/v1/meetings/today');
  if (!res.ok) throw new Error(`Today fetch failed: ${res.status}`);
  const json = await res.json() as { data: { meetings: CalendarMeetingDTO[] } };
  return json.data.meetings;
}

async function fetchMeeting(id: string): Promise<RorosMeetingDTO> {
  const res = await apiFetch(`/api/v1/meetings/${id}`);
  if (!res.ok) throw new Error(`Meeting fetch failed: ${res.status}`);
  const json = await res.json() as { data: RorosMeetingDTO };
  return json.data;
}

// ─── Read Hooks ───────────────────────────────────────────────────────────────

/**
 * AC-FW2-FU1-1: Fetch a paginated list of meetings (GET /api/v1/meetings).
 * Supports optional filters: type, status, accountable_actor_id.
 */
export function useMeetingsList(params?: Record<string, string>) {
  return useQuery({
    queryKey: meetingKeys.list(params),
    queryFn: async (): Promise<RorosMeetingDTO[]> => {
      const qs = params && Object.keys(params).length > 0
        ? '?' + new URLSearchParams(params).toString()
        : '';
      const res = await apiFetch(`/api/v1/meetings${qs}`);
      if (!res.ok) throw new Error(`Meetings list fetch failed: ${res.status}`);
      const json = await res.json() as { data: RorosMeetingDTO[] };
      return json.data;
    },
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/**
 * Fetch meetings for a calendar date window.
 * `from` and `to` are YYYY-MM-DD strings.
 */
export function useRorosMeetingsCalendar(from: string, to: string) {
  return useQuery({
    queryKey: meetingKeys.calendar(from, to),
    queryFn: () => fetchCalendar(from, to),
    enabled: !!from && !!to,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/** Fetch today's meetings for the Control Tower dashboard strip. */
export function useRorosTodaysMeetings() {
  return useQuery({
    queryKey: meetingKeys.today(),
    queryFn: fetchToday,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/** Fetch a single meeting with participants and agenda. */
export function useRorosMeeting(id: string) {
  return useQuery({
    queryKey: meetingKeys.detail(id),
    queryFn: () => fetchMeeting(id),
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ─── Mutation Hooks (M-Rhythm-3) ─────────────────────────────────────────────

/**
 * Create an ad-hoc meeting (POST /api/v1/meetings).
 * On success, invalidates all meeting query keys so the calendar refreshes.
 */
export function useCreateAdHocMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const res = await apiFetch('/api/v1/meetings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        // F2.1+F2.2: log parse errors and use typed response interface
        const data = await res.json().catch((e: unknown) => {
          console.warn('[useCreateAdHocMeeting] Failed to parse error response', e);
          return {} as ApiErrorResponse;
        });
        throw new Error((data as ApiErrorResponse).error?.message ?? 'Failed to create meeting');
      }
      return (await res.json() as { data: RorosMeetingDTO }).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: meetingKeys.all });
    },
  });
}

/**
 * Update an existing meeting (PATCH /api/v1/meetings/:id).
 * On success, invalidates all meeting keys and the detail key for this meeting.
 */
export function useUpdateMeeting(meetingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const res = await apiFetch(`/api/v1/meetings/${meetingId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        // F2.1+F2.2: log parse errors and use typed response interface
        const data = await res.json().catch((e: unknown) => {
          console.warn('[useUpdateMeeting] Failed to parse error response', e);
          return {} as ApiErrorResponse;
        });
        throw new Error((data as ApiErrorResponse).error?.message ?? 'Failed to update meeting');
      }
      return (await res.json() as { data: RorosMeetingDTO }).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: meetingKeys.all });
      qc.invalidateQueries({ queryKey: meetingKeys.detail(meetingId) });
    },
  });
}

/**
 * Skip a meeting with an optional reason (POST /api/v1/meetings/:id/skip).
 * On success, invalidates all meeting keys and the detail key for this meeting.
 */
export function useSkipMeeting(meetingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (reason?: string) => {
      const res = await apiFetch(`/api/v1/meetings/${meetingId}/skip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      if (!res.ok) {
        // F2.1+F2.2: log parse errors and use typed response interface
        const data = await res.json().catch((e: unknown) => {
          console.warn('[useSkipMeeting] Failed to parse error response', e);
          return {} as ApiErrorResponse;
        });
        throw new Error((data as ApiErrorResponse).error?.message ?? 'Failed to skip meeting');
      }
      return (await res.json() as { data: RorosMeetingDTO }).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: meetingKeys.all });
      qc.invalidateQueries({ queryKey: meetingKeys.detail(meetingId) });
    },
  });
}
